import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "../hooks/useQuery";
import { api, ASSETS, BRAND } from "../lib/api";
import JsBarcode from "jsbarcode";
import { Printer, Loader2 } from "lucide-react";

export default function Label() {
  const { id } = useParams();
  const { data, loading, error } = useQuery(() => api.get(`/shipments/${id}/label`).then((r) => r.data), [id]);
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (data && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, data.tracking_id, {
          format: "CODE128",
          width: 3,
          height: 110,
          displayValue: false,
          margin: 0,
          background: "#ffffff",
          lineColor: "#000000",
        });
      } catch (e) {
        // ignore
      }
    }
  }, [data]);

  if (loading) return <div className="p-12 text-center text-[#1B3022]"><Loader2 className="animate-spin mx-auto" /></div>;
  if (error) return <div className="p-12 text-center text-[#C8102E]" data-testid="label-error">Unable to load shipment label.</div>;

  return (
    <div className="min-h-screen bg-[#F5EFE0] py-8 px-4 print:p-0 print:bg-white">
      <div className="max-w-md mx-auto print:max-w-none">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <h1 className="font-serif-bv text-2xl text-[#1B3022]">Thermal Shipping Label</h1>
          <button
            data-testid="print-label-btn"
            onClick={() => window.print()}
            className="btn-gold rounded-sm px-5 py-2.5 text-xs inline-flex items-center gap-2"
          >
            <Printer size={14} /> Print
          </button>
        </div>

        {/* 4x6 inch label — 384x576 px at 96dpi */}
        <div
          data-testid="thermal-label"
          className="bg-white border-2 border-black mx-auto"
          style={{ width: "4in", minHeight: "6in", fontFamily: "Arial, sans-serif", color: "#000" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-black p-2">
            <img src={ASSETS.logo} alt="HBS" style={{ height: "40px" }} />
            <div className="text-right text-[10px] leading-tight">
              <div className="font-bold tracking-widest text-sm">HBS</div>
              <div>{BRAND.name_line1} {BRAND.name_line2}</div>
              <div className="italic">{BRAND.tagline}</div>
            </div>
          </div>

          {/* Service line */}
          <div className="border-b-2 border-black px-2 py-1 flex items-center justify-between">
            <div className="text-[10px]">{BRAND.domain}</div>
            <div className="font-bold text-xs tracking-wider">HBS GROUND ADVANTAGE™</div>
          </div>

          {/* Postage */}
          <div className="grid grid-cols-3 border-b-2 border-black">
            <div className="border-r border-black p-2 text-center">
              <div className="text-[10px]">POSTAGE</div>
              <div className="font-black text-4xl leading-none">G</div>
              <div className="text-[10px] mt-1">{data.method === "Air Freight" ? "Air" : "Cubic"}</div>
            </div>
            <div className="border-r border-black p-2 text-center">
              <div className="text-[10px]">PAID</div>
              <div className="text-xs font-bold">${(data.estimated_cost || 0).toFixed(2)}</div>
              <div className="text-[9px] mt-1">{(data.weight_lbs || 0)} lbs</div>
            </div>
            <div className="p-2 text-center text-[10px]">
              <div>Created</div>
              <div className="font-bold">{(data.created_at || "").slice(0, 10)}</div>
              <div className="mt-1">Mailed {data.origin_city}</div>
            </div>
          </div>

          {/* From */}
          <div className="border-b-2 border-black p-2 text-[11px] leading-tight">
            <div className="text-[9px] tracking-widest font-bold mb-1">FROM:</div>
            <div className="font-bold uppercase">{data.sender_name}</div>
            <div>{data.sender_address || "—"}</div>
            <div className="uppercase">{data.sender_city || data.origin_city}</div>
            {data.sender_phone && <div>{data.sender_phone}</div>}
          </div>

          {/* To — bigger emphasis */}
          <div className="border-b-2 border-black p-2 text-[14px] leading-tight">
            <div className="text-[9px] tracking-widest font-bold mb-1">SHIP TO:</div>
            <div className="font-black text-[17px] uppercase">{data.receiver_name}</div>
            <div className="font-bold">{data.receiver_address || "—"}</div>
            <div className="font-bold uppercase">{data.receiver_city || data.destination_city}</div>
            {data.receiver_phone && <div>{data.receiver_phone}</div>}
          </div>

          {/* Tracking */}
          <div className="p-2 text-center">
            <div className="text-xs font-bold tracking-widest border-b-2 border-black pb-1 mb-2">HBS TRACKING #</div>
            <svg ref={barcodeRef} data-testid="barcode-svg" className="mx-auto" />
            <div className="font-mono text-base tracking-[0.18em] mt-1 font-bold">{data.tracking_id}</div>
          </div>

          <div className="border-t-2 border-black px-2 py-1 text-[9px] flex items-center justify-between">
            <span>BP {(data.tracking_id || "").slice(-5)}</span>
            <span className="italic">{BRAND.motto}</span>
          </div>
          <div className="border-t border-black px-2 py-1 text-[9px] flex items-center justify-between bg-black text-white">
            <span>Customer Support: {BRAND.phone}</span>
            <span>{BRAND.domain}</span>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: 4in 6in; margin: 0; }
          body { margin: 0; }
        }
      `}</style>
    </div>
  );
}
